import { CSSProperties } from 'react';
import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { UserProfession } from '~/enums/UserProfession';
import { useMediaQuery } from '~/hooks';
import { ProfessionalNameRow } from '../shared/ProfessionalNameRow';
import { ActivityLevelInfo } from '../shared/ActivityLevelInfo';
import AttendanceBadgeInline from '../shared/AttendanceBadgeInline';
import { IProfessionalCardUser } from '../../interfaces';
import { BULLET } from '../shared/cardText';

// ── inline styles (no DS prop equivalent) ──────────────────────────────────
/**
 * flex/minWidth/marginLeft have no DS Box prop. NOTE(future-DS): the marginLeft spaces this
 * column from the avatar in the parent <ProfessionalCard>; ideally a gap on the parent's row.
 */
const detailsColumnStyle: CSSProperties = { marginLeft: 16, flex: 1, minWidth: 0 };
/** whiteSpace has no DS Row prop; keeps each info line on one line */
const nowrapRowStyle: CSSProperties = { whiteSpace: 'nowrap' };

interface IProfessionalCardDetailsProps {
  user: IProfessionalCardUser;
}

export const ProfessionalCardDetails = ({ user }: IProfessionalCardDetailsProps) => {
  const {
    id,
    fullname,
    profession,
    rating,
    isAvailable,
    activityLevel,
    yearsOfExperience,
    completedJobs,
    badges,
  } = user;
  const textColor = isAvailable ? colors.neutral['500'] : colors.neutral['300'];
  const hasBadges = badges && badges.length > 0;
  const [isSmallScreen] = useMediaQuery(['mlg']);

  return (
    <Column justify="center" style={detailsColumnStyle}>
      {/* Row 1: Name + Rating + Profile */}
      <ProfessionalNameRow id={id} fullname={fullname} rating={rating} isAvailable={isAvailable} />

      {isSmallScreen ? (
        <>
          {/* Small screen — Line 1: Profession • Experience */}
          <Row align="center" style={nowrapRowStyle}>
            <Typography color={textColor}>{UserProfession.getName(profession || '')}</Typography>
            {yearsOfExperience != null && (
              <Typography color={textColor}>
                {BULLET}
                {yearsOfExperience} yr{yearsOfExperience !== 1 ? 's' : ''} Experience
              </Typography>
            )}
          </Row>

          {/* Small screen — Line 2: Completed Shifts • Activity Level */}
          <Row align="center" style={nowrapRowStyle}>
            {completedJobs != null && (
              <Typography color={textColor}>
                Completed Shifts:{' '}
                <Typography as="span" bold color={textColor}>
                  {completedJobs}
                </Typography>
              </Typography>
            )}
            {completedJobs != null && activityLevel && (
              <Typography color={textColor}>{BULLET}</Typography>
            )}
            {activityLevel && (
              <ActivityLevelInfo activityLevel={activityLevel} isAvailable={isAvailable} />
            )}
          </Row>

          {/* Small screen — Line 3: Attendance */}
          {hasBadges && (
            <Row align="center" gap={1} style={nowrapRowStyle}>
              <Typography color={textColor}>Attendance:</Typography>
              <AttendanceBadgeInline badges={badges} />
            </Row>
          )}
        </>
      ) : (
        <>
          {/* Big screen — Line 1: Profession • Experience • Completed Shifts */}
          <Row align="center" style={nowrapRowStyle}>
            <Typography color={textColor}>{UserProfession.getName(profession || '')}</Typography>
            {yearsOfExperience != null && (
              <Typography color={textColor}>
                {BULLET}
                {yearsOfExperience} yr{yearsOfExperience !== 1 ? 's' : ''} Experience
              </Typography>
            )}
            {completedJobs != null && (
              <Typography color={textColor}>
                {BULLET} Completed Shifts:{' '}
                <Typography as="span" bold color={textColor}>
                  {completedJobs}
                </Typography>
              </Typography>
            )}
          </Row>

          {/* Big screen — Line 2: Activity Level • Attendance */}
          <Row align="center" style={nowrapRowStyle}>
            {activityLevel && (
              <ActivityLevelInfo activityLevel={activityLevel} isAvailable={isAvailable} />
            )}
            {hasBadges && activityLevel && <Typography color={textColor}>{BULLET}</Typography>}
            {hasBadges && (
              <Row align="center" gap={1}>
                <Typography color={textColor}>Attendance:</Typography>
                <AttendanceBadgeInline badges={badges} />
              </Row>
            )}
          </Row>
        </>
      )}
    </Column>
  );
};
